import { BACKEND_API_URL, authHeaders } from "@/lib/api"

export type OnboardingPathRow = {
  path_id: string
  enabled: boolean
  rank: number | null
  updated_at: string
  updated_by: string
}

export type OnboardingPathsResponse = {
  ids: string[]
  rows: OnboardingPathRow[]
  connect_tools: { socials: boolean }
  generated_at: string
}

export type OnboardingPathSave = {
  enabled: boolean
  rank: number | null
  expected_updated_at: string
}

export const GATE_IDS = ["gmail_readonly"]

export class OnboardingPathSaveError extends Error {
  status: number
  code: string
  constructor(status: number, code: string) {
    super(code)
    this.status = status
    this.code = code
  }
}

export type OnboardingMetric = {
  numerator: number | null
  denominator: number | null
  value: number | null
  unknown: boolean
  median_seconds?: number | null
  p90_seconds?: number | null
  no_aha_yet?: number | null
}

export type OnboardingPathOptIn = {
  unknown: boolean
  paths: Record<string, OnboardingMetric>
}

export type OnboardingMetricsResponse = {
  days: number
  since: string
  cohort_size: number | null
  metrics: {
    time_to_aha: OnboardingMetric
    first_reply_rate: OnboardingMetric
    path_opt_in: OnboardingPathOptIn
    aha_reaction: OnboardingMetric
    first_session_booking: OnboardingMetric
    drop_off: OnboardingMetric
    card_24h: OnboardingMetric
    d1: OnboardingMetric
    d7: OnboardingMetric
    d30: OnboardingMetric
  }
  posthog: { event: string; count: number; people: number }[] | null
  posthog_error: string | null
  generated_at: string
  truncated: boolean
}

export async function fetchOnboardingPathMetrics(days: number): Promise<OnboardingMetricsResponse> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/onboarding_paths/metrics?days=${days}`,
    { headers: await authHeaders() },
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || err.detail || "Failed to load onboarding metrics")
  }
  return response.json()
}

export async function fetchOnboardingPaths(): Promise<OnboardingPathsResponse> {
  const response = await fetch(`${BACKEND_API_URL}/admin/onboarding_paths`, {
    headers: await authHeaders(),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.error || err.detail || "Failed to load onboarding paths")
  }
  return response.json()
}

export async function saveOnboardingPath(
  pathId: string,
  body: OnboardingPathSave,
): Promise<OnboardingPathRow> {
  const response = await fetch(
    `${BACKEND_API_URL}/admin/onboarding_paths/${encodeURIComponent(pathId)}`,
    {
      method: "PUT",
      headers: await authHeaders(),
      body: JSON.stringify(body),
    },
  )
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    const code = typeof err.error === "string" ? err.error : "unknown"
    throw new OnboardingPathSaveError(response.status, code)
  }
  const payload = await response.json()
  return payload.row as OnboardingPathRow
}
